"use client";

import { useTranslations } from "next-intl";
import { Alert, Stack, Text } from "@leabankab/component-library";
import {
	CheckboxConsentField,
	ConfirmationButton,
	FormContainer,
} from "@/components/form";
import { SectionHeading } from "@/components/ui";
import { useSubmitTask } from "@/hooks";
import { TaskType } from "@/types";
import { AddedDebtList } from "./components/added-debt-list";
import { FetchedDebtList } from "./components/fetched-debt-list";
import { useConfirmDebtForm } from "./hooks/use-confirm-debt-form";
import { ConfirmDebtPayload, ResolvedConfirmDebtContext } from "./types";
import { toConfirmDebtPayload } from "./utils/confirm-debt-payload";

interface Props {
	taskId: string;
	context: ResolvedConfirmDebtContext;
}

const ConfirmDebtScreen = ({ taskId, context }: Props) => {
	const t = useTranslations("pages.ConfirmDebtPage");
	const { useForm: methods, allowedTypes } = useConfirmDebtForm();
	const { handleSubmit } = methods;

	const submit = useSubmitTask<ConfirmDebtPayload>({
		taskId,
		processType: context.process,
		taskType: TaskType.ConfirmDebt,
	});

	const onSubmit = handleSubmit((values) =>
		submit(toConfirmDebtPayload(values, context))
	);

	return (
		<FormContainer
			methods={methods}
			onSubmit={onSubmit}
			getPayloadPreview={(values) => toConfirmDebtPayload(values, context)}
		>
			<Text textStyle="body-md" color="text.base">
				{t("intro")}
			</Text>

			<Stack gap={1}>
				<SectionHeading>{t("fetched.heading")}</SectionHeading>
				<FetchedDebtList debts={context.debts} />
			</Stack>
			<Alert severity="info" size="sm">
				{t("contactInfo")}
			</Alert>

			<Stack gap={0.5}>
				<SectionHeading>{t("added.heading")}</SectionHeading>
				<Text textStyle="body-sm">{t("added.description")}</Text>
				<AddedDebtList allowedTypes={allowedTypes} />
			</Stack>

			<CheckboxConsentField name="confirm" label={t("confirmCheckbox")} />

			<ConfirmationButton type="submit" label={t("button.continue")} />
		</FormContainer>
	);
};

export default ConfirmDebtScreen;
